import React, { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, Expand } from "lucide-react";
import { browserFullScreenAvailable, toggleBrowserFullScreen } from "@/lib/fullScreenMode";   // Batch 23 · Stage 6(b)
import { useI18n } from "@/contexts/I18nContext";

/**
 * Round 8 combined · W1 — one shared full-screen dialog shell for
 * every worksheet / form / list dialog in the portal.
 *
 * Owner decision: replaces the small centred modal. The dialog takes
 * the full page width + height; inner content scrolls; the top-right
 * carries a clear Close / Back button (EN + AR, RTL mirrors), and
 * Esc closes. Closing calls ``onClose`` which the parent uses to
 * drop the deep-link params so the browser returns to the exact
 * origin (same head folder, expanded accordion, scroll position —
 * N2). Props pass straight through to Radix Dialog so existing
 * dialogs can be migrated with a one-line swap.
 */
export default function FullScreenDialog({ open, onOpenChange, onClose,
                                                title, titleBadge, headerExtra,
                                                children, testid }) {
  const { t } = useI18n();
  const closeRef = useRef(null);
  // Esc close is handled natively by Radix; this effect auto-focuses
  // the Close button so screen-reader users land somewhere actionable.
  useEffect(() => { if (open) setTimeout(() => closeRef.current?.focus(), 50); }, [open]);
  const handleOpen = (v) => {
    onOpenChange?.(v);
    if (!v) onClose?.();
  };
  return (
    <Dialog open={!!open} onOpenChange={handleOpen}>
      <DialogContent
        data-testid={testid || "full-screen-dialog"}
        hideCloseButton
        className="h-screen w-screen max-w-none rounded-none p-0 gap-0 flex flex-col sm:rounded-none"
        style={{ left: 0, top: 0, transform: "none" }}>
        <DialogHeader className="shrink-0 border-b px-4 py-2.5 flex-row items-center justify-between gap-3 space-y-0">
          <DialogTitle className="flex min-w-0 items-center gap-2 pe-8 text-base">
            {titleBadge}
            <span className="min-w-0 break-words">{title}</span>{/* Batch 23 · Stage 3: the title wraps instead of being cut */}
          </DialogTitle>
          <div className="flex shrink-0 items-center gap-2">
            {headerExtra}
            {browserFullScreenAvailable() && (
              <Button type="button" variant="ghost" size="sm" onClick={toggleBrowserFullScreen} className="h-8 gap-1 text-xs"
                      data-testid={`${testid || "full-screen-dialog"}-browser-fullscreen`} title={t("common.browserFullScreen")}>
                <Expand className="h-4 w-4" /><span className="hidden lg:inline">{t("common.browserFullScreen")}</span>
              </Button>
            )}
            <Button ref={closeRef} type="button" variant="ghost" size="sm"
                    onClick={() => handleOpen(false)}
                    data-testid={`${testid || "full-screen-dialog"}-close`}
                    className="h-8 gap-1 text-xs">
              <X className="h-4 w-4" />
              <span>{t("common.close") || "Close"}</span>
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 overflow-auto px-4 py-3" data-testid={`${testid || "full-screen-dialog"}-body`}>
          {children}
        </div>
      </DialogContent>
    </Dialog>
  );
}
